import React, { useMemo, useState } from 'react';
import { DISCOVERY_INTERESTS, getInterestLabels, normalizeInterestIds } from '../services/discoveryConfig';

function InterestSettings({ initialInterestIds = [], mode = 'settings', onSave, onCancel }) {
  const [selectedInterestIds, setSelectedInterestIds] = useState(() => normalizeInterestIds(initialInterestIds));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const selectedLabels = useMemo(() => getInterestLabels(selectedInterestIds), [selectedInterestIds]);
  const isOnboarding = mode === 'onboarding';

  const toggleInterest = (interestId) => {
    setSelectedInterestIds((currentIds) => {
      const current = normalizeInterestIds(currentIds);
      return current.includes(interestId)
        ? current.filter((id) => id !== interestId)
        : [...current, interestId];
    });
    setError('');
  };

  const handleSave = async () => {
    const normalizedIds = normalizeInterestIds(selectedInterestIds);
    if (normalizedIds.length === 0) {
      setError('Choose at least one interest to personalize Discovery.');
      return;
    }

    setSaving(true);
    setError('');
    try {
      await onSave(normalizedIds);
    } catch (saveError) {
      setError(saveError.message || 'Interests could not be saved.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={pageStyle}>
      <section style={panelStyle}>
        <p style={eyebrowStyle}>{isOnboarding ? 'Discovery setup' : 'Profile settings'}</p>
        <h1 style={headingStyle}>{isOnboarding ? 'Choose What You Want To Discover' : 'Discovery Interests'}</h1>
        <p style={copyStyle}>
          These interests personalize your PromoGrind Discovery feed. They are not your profession or creator type.
        </p>

        <div style={interestGridStyle}>
          {DISCOVERY_INTERESTS.map((interest) => {
            const selected = selectedInterestIds.includes(interest.id);
            return (
              <label key={interest.id} style={interestOptionStyle(selected)}>
                <input
                  type="checkbox"
                  checked={selected}
                  onChange={() => toggleInterest(interest.id)}
                />
                <span>{interest.label}</span>
              </label>
            );
          })}
        </div>

        <div style={summaryStyle}>
          <strong>Selected:</strong> {selectedLabels.length > 0 ? selectedLabels.join(', ') : 'None yet'}
        </div>

        {error && <div role="alert" style={errorStyle}>{error}</div>}

        <div style={actionsStyle}>
          <button type="button" onClick={handleSave} disabled={saving} style={primaryButtonStyle(saving)}>
            {saving ? 'Saving...' : isOnboarding ? 'Start Discovery' : 'Save Interests'}
          </button>
          {!isOnboarding && (
            <button type="button" onClick={onCancel} disabled={saving} style={secondaryButtonStyle}>
              Cancel
            </button>
          )}
        </div>
      </section>
    </div>
  );
}

const pageStyle = {
  minHeight: 'calc(100vh - 76px)',
  padding: '32px 16px',
  backgroundColor: '#f5f5f5'
};

const panelStyle = {
  backgroundColor: '#ffffff',
  border: '1px solid #dbe4ee',
  borderRadius: 10,
  boxShadow: '0 12px 30px rgba(15, 23, 42, 0.08)',
  margin: '0 auto',
  maxWidth: 860,
  padding: 28
};

const eyebrowStyle = {
  color: '#0f766e',
  fontSize: 13,
  fontWeight: 800,
  letterSpacing: 0,
  margin: '0 0 8px',
  textTransform: 'uppercase'
};

const headingStyle = {
  color: '#111827',
  fontSize: 30,
  margin: '0 0 10px'
};

const copyStyle = {
  color: '#526070',
  fontSize: 16,
  lineHeight: 1.5,
  margin: '0 0 20px'
};

const interestGridStyle = {
  display: 'grid',
  gap: 10,
  gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))'
};

const interestOptionStyle = (selected) => ({
  alignItems: 'center',
  backgroundColor: selected ? '#eef7ff' : '#ffffff',
  border: `1px solid ${selected ? '#007bff' : '#d8dee6'}`,
  borderRadius: 8,
  color: '#25364a',
  cursor: 'pointer',
  display: 'flex',
  fontSize: 15,
  fontWeight: selected ? 'bold' : 'normal',
  gap: 10,
  minHeight: 46,
  padding: '10px 12px'
});

const summaryStyle = {
  backgroundColor: '#f8f9fa',
  border: '1px solid #d8dee6',
  borderRadius: 8,
  color: '#25364a',
  marginTop: 18,
  padding: 12
};

const errorStyle = {
  backgroundColor: '#fff3f3',
  border: '1px solid #f1aeb5',
  borderRadius: 8,
  color: '#842029',
  fontWeight: 'bold',
  marginTop: 14,
  padding: 12
};

const actionsStyle = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 12,
  marginTop: 20
};

const primaryButtonStyle = (saving) => ({
  backgroundColor: saving ? '#94a3b8' : '#0f766e',
  border: 'none',
  borderRadius: 8,
  color: '#ffffff',
  cursor: saving ? 'not-allowed' : 'pointer',
  fontSize: 16,
  fontWeight: 'bold',
  padding: '12px 18px'
});

const secondaryButtonStyle = {
  backgroundColor: '#ffffff',
  border: '1px solid #bfdbfe',
  borderRadius: 8,
  color: '#2563eb',
  cursor: 'pointer',
  fontSize: 16,
  fontWeight: 'bold',
  padding: '12px 18px'
};

export default InterestSettings;
